import { Controller } from '@hotwired/stimulus';

/** Matches `_home_hero.scss` — stroke-dasharray end value (pathLength=100 on the path). */
export const HOME_HERO_ROUTE_PROGRESS_END_VAR = '--hero-route-progress-end';

/**
 * Approximate inverse of the stroke keyframes' `ease-out` so marker delays line up with
 * the growing dash (cubic ease-out is close to CSS `ease-out`).
 */
function easeOutInverse(fraction: number): number {
  const y = Math.min(1, Math.max(0, fraction));

  return 1 - (1 - y) ** (1 / 3);
}

/** Seconds until the route stroke reaches `at` (0–1), matching --hero-route-anim-* on `.homepage-hero__map`. */
function markerRevealDelaySec(
  at: number,
  progress: number,
  delaySec: number,
  durationSec: number
): number {
  if (progress <= 0) {
    return delaySec;
  }

  const y = Math.min(1, Math.max(0, at / progress));

  return delaySec + easeOutInverse(y) * durationSec;
}

/**
 * Builds the hero route from a hand-tuned SVG path (viewBox 0–100), places markers,
 * and sets `--hero-route-progress-end` for the completed-route stroke animation.
 */
export default class HomeHeroMapController extends Controller {
  public static readonly targets = [
    'routePath',
    'routeRemaining',
    'markerLayer'
  ];

  public static readonly values = {
    progress: { type: Number, default: 0.42 },
    markers: { type: Array, default: [] },
    /** e.g. trail traced on a tilted photo). */
    svgPath: { type: String, default: '' }
  };

  declare private readonly routePathTarget: SVGPathElement;

  declare private readonly hasRoutePathTarget: boolean;

  declare private readonly routeRemainingTarget: SVGPathElement;

  declare private readonly markerLayerTarget: HTMLElement;

  declare public svgPathValue: string;

  declare public progressValue: number;

  declare public markersValue: Array<{
    at: number;
    kind: 'checkpoint' | 'current';
  }>;

  private intersectionObserver: IntersectionObserver | null = null;

  public connect(): void {
    const svgPath = this.svgPathValue.trim();

    if (svgPath.length > 0) {
      this.routeRemainingTarget.setAttribute('d', svgPath);
      this.routePathTarget.setAttribute('d', svgPath);
    }

    this._syncRouteProgressCss();

    this.markerLayerTarget.replaceChildren();
    const path = this.routePathTarget;
    const length = path.getTotalLength();
    const { delaySec, durationSec } = this._readRouteAnimationTiming();
    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const progress = this._clampedProgress();

    for (const marker of this.markersValue) {
      const at = Number(marker.at);

      if (!Number.isFinite(at) || at < 0 || at > progress + 1e-6) {
        continue;
      }

      const point = path.getPointAtLength(length * at);
      const el = document.createElement('div');

      el.className = `homepage-hero__marker homepage-hero__marker--${marker.kind}`;
      el.setAttribute('aria-hidden', 'true');
      el.style.left = `${point.x}%`;
      el.style.top = `${point.y}%`;

      if (!prefersReducedMotion) {
        const revealSec = markerRevealDelaySec(
          at,
          progress,
          delaySec,
          durationSec
        );

        el.style.setProperty('--hero-marker-delay', `${revealSec}s`);
      }

      this.markerLayerTarget.appendChild(el);
    }

    this._setupVisibilityAnimation();
  }

  public disconnect(): void {
    this.intersectionObserver?.disconnect();
    this.intersectionObserver = null;
  }

  public progressValueChanged(): void {
    if (!this.hasRoutePathTarget) {
      return;
    }

    this._syncRouteProgressCss();
  }

  private _syncRouteProgressCss(): void {
    const clamped = this._clampedProgress();
    const dashEnd = clamped * 100;

    this.routePathTarget.style.setProperty(
      HOME_HERO_ROUTE_PROGRESS_END_VAR,
      String(dashEnd)
    );
  }

  private _clampedProgress(): number {
    const t = Number.isFinite(this.progressValue) ? this.progressValue : 0.42;

    return Math.min(1, Math.max(0, t));
  }

  private _readRouteAnimationTiming(): {
    delaySec: number;
    durationSec: number;
  } {
    const raw = getComputedStyle(this.element);
    const delaySec =
      parseFloat(raw.getPropertyValue('--hero-route-anim-delay')) || 0.15;
    const durationSec =
      parseFloat(raw.getPropertyValue('--hero-route-anim-duration')) || 2.2;

    return { delaySec, durationSec };
  }

  /**
   * Starts route/marker CSS animations when the map enters the viewport so
   * below-the-fold layouts (e.g. small screens) do not finish before the user scrolls.
   */
  private _setupVisibilityAnimation(): void {
    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) {
      return;
    }

    if (typeof IntersectionObserver === 'undefined') {
      this.element.classList.add('homepage-hero__map--in-view');

      return;
    }

    this.intersectionObserver = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.element.classList.add('homepage-hero__map--in-view');
            this.intersectionObserver?.disconnect();
            this.intersectionObserver = null;

            break;
          }
        }
      },
      { root: null, rootMargin: '0px 0px -5% 0px', threshold: 0.15 }
    );

    this.intersectionObserver.observe(this.element);
  }
}
